import { useRef } from 'react';
import { translate } from '@washed/i18n';
import { useReveal } from '../motion/useReveal.js';
import { Reveal } from '../motion/Reveal.js';
import { MaskedReveal } from '../motion/MaskedReveal.js';
import { brandify } from '../components/Brand.js';
import styles from './Compare.module.css';

export interface CompareProps {
  locale: 'fr' | 'en';
}

export function Compare({ locale }: CompareProps) {
  const t = (k: Parameters<typeof translate>[0]) => translate(k, locale);
  const cardsRef = useRef<HTMLDivElement>(null);
  const inView = useReveal(cardsRef as React.RefObject<Element | null>);
  const sansItems = ([1, 2, 3, 4, 5] as const).map((i) => t(`marketing.compare.sans.item${i}`));
  const avecItems = ([1, 2, 3, 4, 5] as const).map((i) => t(`marketing.compare.avec.item${i}`));

  const sansClasses = [styles.card, styles.sans, styles.fromLeft, inView ? styles.in : '']
    .filter(Boolean)
    .join(' ');

  const avecClasses = [styles.card, styles.avec, styles.fromRight, inView ? styles.in : '']
    .filter(Boolean)
    .join(' ');

  return (
    <section id="compare" className={styles.compare}>
      <header className={styles.head}>
        <Reveal as="h2" variant="mask">
          <MaskedReveal>{t('marketing.compare.head.title')}</MaskedReveal>
        </Reveal>
        <p>{brandify(t('marketing.compare.head.body'))}</p>
        <p className={styles.headNote}>{t('marketing.compare.head.note')}</p>
      </header>

      <div ref={cardsRef} className={styles.cards}>
        <article className={sansClasses}>
          <span className={styles.tag}>{brandify(t('marketing.compare.sans.tag'))}</span>
          <h3>{t('marketing.compare.sans.h3')}</h3>
          <ul className={styles.list}>
            {sansItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <footer className={styles.summary}>
            <span className={styles.summaryLabel}>{t('marketing.compare.summary.label')}</span>
            <span className={styles.summaryValue}>{t('marketing.compare.summary.sans')}</span>
          </footer>
        </article>

        <article className={avecClasses}>
          <span className={styles.tag}>{brandify(t('marketing.compare.avec.tag'))}</span>
          <h3>{t('marketing.compare.avec.h3')}</h3>
          <ul className={styles.list}>
            {avecItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
          <footer className={styles.summary}>
            <span className={styles.summaryLabel}>{t('marketing.compare.summary.label')}</span>
            <span className={styles.summaryValue}>{t('marketing.compare.summary.avec')}</span>
          </footer>
        </article>
      </div>
    </section>
  );
}
